import React from 'react';
import { BrowserRouter } from "react-router-dom";
import NavigationBar from '../Nav/Nav';
import { Route, Routes } from 'react-router-dom';
import ErrorHandler from '../ErrorHandler/ErrorHandler';
import Root from '../Root/Root';
import { IntlProvider } from 'react-intl';
import locale_de from "../translations/de.json";
import locale_en from "../translations/en.json";
import locale_fr from "../translations/fr.json";
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { IdentityResource, ProjectResource, ProjectThemeResource } from '../generated/cortex-api';
import { Environment, withCredentialsRetries } from '../Environment';
import { Theme } from '@mui/system';
import { createTheme } from '@mui/material/styles';
import { useTheme } from "@mui/material";
import './fonts.css'
import { PlausiblePageviews } from '../analytics/PlausiblePageviews';

const languages = new Map([
  ["de", locale_de],
  ["en", locale_en],
  ["fr", locale_fr]
]);

function getLanguage(language: string): string {
  return languages.has(language) ? language : languages.has(language.split(/[-_]/)[0]) ? language.split(/[-_]/)[0] : 'en'
}

const language = getLanguage(navigator.language)

const kodoTheme = (primaryColor: string, secondaryColor: string) => {
  return createTheme({
    typography: {
      fontFamily: ['"Helvetica Neue LT W05 65 Medium"', 'helvetica', 'Arial'].join(','),
      h1: {
        fontFamily: ['"Helvetica Neue LT W05_77 Bd Cn"', 'helvetica', 'Arial'].join(','),
        fontSize: '3rem',
        fontWeight: 'bold',
        marginBottom: '1rem',
        textAlign: 'center',
      },
      h2: {
        fontFamily: ['"Helvetica Neue LT W05_77 Bd Cn"', 'helvetica', 'Arial'].join(','),
        fontSize: '2.5rem',
        fontWeight: 'bold',
        marginBottom: '1rem',
        textAlign: 'center',
      },
      h3: {
        fontFamily: ['"Helvetica Neue LT W05_77 Bd Cn"', 'helvetica', 'Arial'].join(','),
        fontSize: '2rem',
        fontWeight: 'bold',
        marginBottom: '1rem',
      },
      h4: {
        fontFamily: ['"Helvetica Neue LT W05_47 Lt Cn"', 'helvetica', 'Arial'].join(','),
        marginBottom: '1rem',
      },
      h5: {
        fontFamily: ['"Helvetica Neue LT W05 75 Bold"', 'helvetica', 'Arial'].join(','),
        fontWeight: 'bold',
        marginBottom: '1rem',
      },
      body1: {
        fontFamily: ['"Helvetica Neue LT W05_47 Lt Cn"', 'helvetica', 'Arial'].join(','),
        marginBottom: '1rem',
        flexWrap: 'wrap',
      },
      body2: {
        fontFamily: ['"Helvetica Neue LT W05_47 Lt Cn"', 'helvetica', 'Arial'].join(','),
        marginBottom: '1rem',
        flexWrap: 'wrap',
      },
      button: {
        fontFamily: ['"Helvetica Neue LT W05_77 Bd Cn"', 'helvetica', 'Arial'].join(','),
        fontWeight: 'bold',
      },
    },
    palette: {
      primary: {
        main: primaryColor
      },
      secondary: {
        main: secondaryColor
      },
    },
    components: {
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: primaryColor,
            '& .MuiTabs-indicator': {
              backgroundColor: 'white',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: 'white',
            '&.Mui-selected': {
              color: 'white',
            },
            '&:hover': {
              color: 'white',
            },
          },
        }
      },
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: { backgroundColor: primaryColor },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            color: 'white',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 30,
            fontWeight: 'bold',
            fontFamily: ['"Helvetica Neue LT W05 75 Bold"', 'helvetica', 'Arial'].join(','),
          },
        },
      },
    },
  });
}


const App = () => {
  //const [loginUrl, setLoginURL] = React.useState<string | undefined>(undefined)
  //const [logoutUrl, setLogoutURL] = React.useState<string | undefined>(undefined)
  const [configuration, setConfiguration] = React.useState<[string, string][] | undefined>(undefined)
  const [identity, setIdentity] = React.useState<IdentityResource | undefined>(undefined)
  const [environment, setEnvironment] = React.useState<Environment | undefined>(undefined)
  const [projects, setProjects] = React.useState<[string, string][] | undefined>(undefined)
//  const [project, setProject] = React.useState<ProjectResource | undefined>(undefined)
  const [theme, setTheme] = React.useState<Theme>(useTheme())
  const [display, setDisplay] = React.useState<string>('none')
  const [projectTheme, setProjectTheme] = React.useState<ProjectThemeResource | undefined>(undefined)

  const [components, setComponents] = React.useState<Set<string> | null>(null);

  const logout = () => {
    environment?.logout()
  }

  const login = () => {
    environment?.login()
  }

  React.useEffect(() => {
    Environment.getInstance().then((environment) => {
      //setLoginURL(environment.loginUrl)
      //setLogoutURL(environment.logoutUrl)
      setEnvironment(environment)
    })
  }, []);


  React.useEffect(() => {
    if(projectTheme && environment) {
        setTheme(kodoTheme(projectTheme.themeColor!, projectTheme.themeColor!))

        if (projectTheme.favIcon?.path) {
          let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement
          if (favicon !== null) {
            favicon.href = environment.launchpadBasePath + projectTheme.favIcon?.path
          } else {
            var link = document.createElement("link") as HTMLLinkElement
            link.rel = "icon"
            link.href = environment.launchpadBasePath + projectTheme.favIcon?.path
            document.head.appendChild(link)
          }
        }
  
        if(projectTheme.appleTouchIcon?.path) {
          let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement
          if (appleTouchIcon !== null) {
            appleTouchIcon.href = environment.launchpadBasePath + projectTheme.appleTouchIcon?.path
          }
        }

        let themeColor = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement
        if (themeColor !== null) {
          themeColor.content = projectTheme.themeColor!
        }
    }
  }, [projectTheme, environment]);

  React.useEffect(() => {
    if(projectTheme && environment) {

      document.title = projectTheme.shortName ?? projectTheme.name!

      let description = document.querySelector('meta[name="description"]') as HTMLMetaElement
      if (description !== null) {
        description.content = projectTheme.description!
      }

      setDisplay('block')

    }
  }, [projectTheme]);

  React.useEffect(() => {
    if(environment) {

      environment.launchpadUserControllerApi.identity(withCredentialsRetries).then((result) => {
        setIdentity(result.data)
      })

      environment.launchpadPublicControllerApi.getProjectTheme1(Number(environment.projectName)).then((result) => {
        setProjectTheme(result.data)
      }).catch(() => {
        setDisplay('block')
      })
    }
  }, [environment]);


  React.useEffect(() => {
    Environment.getInstance().then((environment) => {
  
      const privileges = identity?.roles?.map(t => t?.privileges ?? [])?.reduce((accumulator, value) => accumulator.concat(value), []);
      
      const filteredConfiguration: [string, string][] =
      environment.configuration
        .filter(item =>
          privileges?.some(privilege => privilege.name === item[2])
        )
        .map(it => [it[0], it[1]]);

      let items = projects ? filteredConfiguration.concat(projects) : filteredConfiguration

      setConfiguration(items)
    })
  }, [projects, identity]);

  React.useEffect(() => {
    if (environment) {
      const origins = environment.configuration
        .map(([, url]) => {
          try { return new URL(url).origin; }
          catch { return null; }
        })
        .filter((x): x is string => !!x);

      setComponents(new Set(origins));
    }
  }, [environment]);

  const handleComponentAdminUrl = React.useCallback((event: any) => {
    if (configuration && configuration?.find((value) => new URL(value[1]).hostname === new URL(event.origin).hostname)) {
      if (event.data.type == "projects") {
        setProjects(event.data.content)
      }
    }
  }, [configuration]);

  React.useEffect(() => {
    if (configuration) {
      window.addEventListener('message', handleComponentAdminUrl)
      return () => {
        window.removeEventListener("message", handleComponentAdminUrl);
      };
    }
  }, [configuration]);
  
  // Single-flight refresh
  const refreshInFlightRef = React.useRef<Promise<boolean> | null>(null);
  
  const doRefresh = React.useCallback(async (): Promise<boolean> => {
    try {

      if(environment) {
        await environment.refreshToken()
      }
      return true;
    } catch (e) {
      return false;
    }
  }, [environment]);
  
  const getOrCreateRefresh = React.useCallback((): Promise<boolean> => {
    if (!refreshInFlightRef.current) {
      refreshInFlightRef.current = doRefresh().finally(() => {
        refreshInFlightRef.current = null;
      });
    }
    return refreshInFlightRef.current;
  }, [doRefresh]);

  const handleRefreshToken = React.useCallback(async (event: MessageEvent) => {

    // 1) security: ignore unknown origins
    if (!components || !components.has(event.origin)) return;

    const msg = event.data;
    if (!msg || msg.type !== "refreshToken") return;
  
    // 2) optional: require a requestId to prevent confusion
    const requestId = msg.requestId ?? null;
  
    // 3) run refresh (single-flight)
    const ok = await getOrCreateRefresh();
  
    // 4) respond to the iframe that asked
    // event.source is the window of the iframe
    (event.source as Window | null)?.postMessage(
      { type: "refreshTokenResult", requestId, ok },
      event.origin
    );
  
    // 5) optional: if refresh failed, trigger shell login once
    // if (!ok) login();
  }, [components, getOrCreateRefresh]);

  React.useEffect(() => {
    if (configuration) {
      window.addEventListener('message', handleRefreshToken)
      return () => {
        window.removeEventListener("message", handleRefreshToken);
      };
    }
  }, [configuration]);

  return (
    <div>
        <MuiThemeProvider theme={theme}>
          <BrowserRouter>
            <PlausiblePageviews />
            <IntlProvider locale={language} messages={languages.get(language)}>
                <div style={{ display: display }}>
                  <NavigationBar login={login} logout={logout} isAuthenticated={identity !== undefined} homeURL={"/"} /*loginURL={loginUrl} logoutUrl={logoutUrl}*/ logoUrl={environment && projectTheme?.logo ? environment.launchpadBasePath + projectTheme?.logo?.path : undefined} />
                </div>
              <Routes>
                <Route path="/" element={<Root configuration={configuration} />} />
              </Routes>
              <ErrorHandler login={login} configuration={configuration}/>
            </IntlProvider>
          </BrowserRouter>
      </MuiThemeProvider>
    </div>
  )
}

export default App